// components/features/support/tickets/TicketForm.tsx
import { useState, useEffect, useRef } from "react";
import useClients from "../../../hooks/features/clients/useClients";
import { Ticket, TicketSource } from "../../../types/features/Ticket";
import useProblemCategories from "../../../hooks/features/tickets/useProblemCategories";
import { useTicketDraft } from "../../../hooks/features/tickets/useTicketDraft";

interface TicketFormProps {
  initialData?: Ticket;
  onSubmit: (data: Omit<Ticket, "id" | "created_at" | "updated_at">) => Promise<void>;
  isLoading?: boolean;
  source?: TicketSource;
  onDraftRestored?: () => void;
}

export default function TicketForm({
  initialData,
  onSubmit,
  isLoading = false,
  source = "dashboard",
  onDraftRestored,
}: TicketFormProps) {
  const { clients } = useClients();
  const { categories } = useProblemCategories();
  const { draft, saveDraft, clearDraft, hasDraft } = useTicketDraft();

  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  const [customSlug, setCustomSlug] = useState("");
  const [customName, setCustomName] = useState("");
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [isDraftSaved, setIsDraftSaved] = useState(false);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [formData, setFormData] = useState({
    client_id: "",
    title: "",
    description: "",
    solution: "",
    priority: "moyenne" as "basse" | "moyenne" | "haute" | "critique",
    status: "nouveau" as "nouveau" | "en_cours" | "en_attente" | "escalade" | "resolu" | "ferme",
    source: source,
    agent_id: null as string | null,
    resolved_at: null as string | null,
  });

  // Charger le brouillon s'il existe
  useEffect(() => {
    if (hasDraft && !initialData) {
      setShowRestorePrompt(true);
    }
  }, [hasDraft, initialData]);

  // Restaurer le brouillon
  const restoreDraft = () => {
    if (draft) {
      setFormData({
        client_id: draft.client_id ? String(draft.client_id) : "",
        title: draft.title || "",
        description: draft.description || "",
        solution: draft.solution || "",
        priority: draft.priority || "moyenne",
        status: draft.status || "nouveau",
        source: draft.source || source,
        agent_id: draft.agent_id || null,
        resolved_at: draft.resolved_at || null,
      });
      setSelectedSlugs(draft.problem_categories || []);
      setShowRestorePrompt(false);
      setIsDraftSaved(true);
      if (onDraftRestored) onDraftRestored();
    }
  };

  // Ignorer le brouillon
  const ignoreDraft = () => {
    clearDraft();
    setShowRestorePrompt(false);
    setIsDraftSaved(false);
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        client_id: initialData.client_id ? String(initialData.client_id) : "",
        title: initialData.title,
        description: initialData.description,
        solution: initialData.solution || "",
        priority: initialData.priority,
        status: initialData.status,
        source: initialData.source,
        agent_id: initialData.agent_id,
        resolved_at: initialData.resolved_at,
      });
      setSelectedSlugs(initialData.problem_categories || []);
    }
  }, [initialData]);

  // Sauvegarde automatique (debounced)
  useEffect(() => {
    if (initialData) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Vérifier si le formulaire a des données
    const hasData = formData.client_id || formData.title || formData.description || selectedSlugs.length > 0;
    if (!hasData) return;

    saveTimeoutRef.current = setTimeout(() => {
      const draftData = {
        ...formData,
        problem_categories: selectedSlugs,
      };
      saveDraft(draftData);
      setIsDraftSaved(true);
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData, selectedSlugs, initialData]);

  // Mettre à jour la source quand la prop change
  useEffect(() => {
    setFormData((prev) => ({ ...prev, source }));
  }, [source]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearDraft();
    await onSubmit({
      ...formData,
      problem_categories: selectedSlugs,
      order: 0,
      position: null,
      entreprise_id: "",
    });
  };

  const toggleCategory = (slug: string) => {
    if (selectedSlugs.includes(slug)) {
      setSelectedSlugs(selectedSlugs.filter((s) => s !== slug));
    } else {
      setSelectedSlugs([...selectedSlugs, slug]);
    }
  };

  const addCustomCategory = () => {
    if (customName.trim() && customSlug.trim()) {
      const slug = customSlug.trim().toLowerCase().replace(/\s+/g, "-");
      if (!selectedSlugs.includes(slug)) {
        setSelectedSlugs([...selectedSlugs, slug]);
      }
      setCustomName("");
      setCustomSlug("");
    }
  };

  const removeCategory = (slug: string) => {
    setSelectedSlugs(selectedSlugs.filter((s) => s !== slug));
  };

  const getCategoryName = (slug: string) => {
    const cat = categories.find((c) => c.slug === slug);
    return cat ? cat.name : slug;
  };

  return (
    <>
      {/* Prompt de restauration du brouillon */}
      {showRestorePrompt && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800">Vous avez un brouillon non terminé</p>
              <p className="text-xs text-yellow-600 mt-1">
                {draft?.title ? `"${draft.title}"` : "Brouillon sauvegardé"} -{" "}
                {draft?.updated_at ? new Date(draft.updated_at).toLocaleString("fr-FR") : ""}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={restoreDraft}
                className="px-3 py-1 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
              >
                Restaurer
              </button>
              <button
                type="button"
                onClick={ignoreDraft}
                className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Ignorer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Indicateur de sauvegarde automatique */}
      {!initialData && isDraftSaved && !showRestorePrompt && (
        <div className="flex items-center gap-2 text-xs text-green-600 mb-2">
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
          Brouillon sauvegardé automatiquement
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Client */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
          <select
            name="client_id"
            value={String(formData.client_id || "")}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="">Sélectionner un client</option>
            {clients.map((client) => (
              <option key={client.id} value={String(client.id)}>
                {client.firstname} {client.lastname} - {client.phone || client.email}
              </option>
            ))}
          </select>
        </div>

        {/* Titre */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            placeholder="Ex: Paiement bloqué"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            placeholder="Décrivez le problème..."
          />
        </div>

        {/* Catégories du problème */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Catégories du problème</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => toggleCategory(cat.slug!)}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  selectedSlugs.includes(cat.slug!)
                    ? "bg-green-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Ajouter une catégorie personnalisée */}
          <div className="flex gap-2 mt-2">
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Nom de la catégorie"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
            <input
              type="text"
              value={customSlug}
              onChange={(e) => setCustomSlug(e.target.value)}
              placeholder="slug (sans espaces)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
            <button
              type="button"
              onClick={addCustomCategory}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Ajouter
            </button>
          </div>

          {/* Catégories sélectionnées */}
          {selectedSlugs.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedSlugs.map((slug) => (
                <span
                  key={slug}
                  className="flex items-center gap-1 px-2 py-1 text-sm bg-blue-100 text-blue-700 rounded-full"
                >
                  {getCategoryName(slug)}
                  <button
                    type="button"
                    onClick={() => removeCategory(slug)}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Solution */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Solution (optionnel)</label>
          <textarea
            name="solution"
            value={formData.solution}
            onChange={handleChange}
            rows={3}
            placeholder="Solution apportée au problème..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
        </div>

        {/* Priorité */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
          <select
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="basse">Basse</option>
            <option value="moyenne">Moyenne</option>
            <option value="haute">Haute</option>
            <option value="critique">Critique</option>
          </select>
        </div>

        {/* Boutons */}
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {isLoading ? "Création..." : initialData ? "Modifier" : "Créer le ticket"}
          </button>
        </div>
      </form>
    </>
  );
}
